import LegalPage from "@/components/LegalPage";

const ContentPolicyPage = () => (
  <LegalPage
    title="Content Policy"
    updated="June 18, 2026"
    intro={
      <p>
        Vecurate exists so students and trainees can learn from each other's honest, firsthand
        experiences. This Content Policy explains what may and may not be posted on Vecurate, and how we
        moderate content. It supplements our <a href="/terms">Terms of Service</a> and{" "}
        <a href="/community-guidelines">Community Guidelines</a>.
      </p>
    }
  >
    <h2>1. Core Principles</h2>
    <ul>
      <li>Reviews must be based on your own firsthand experience.</li>
      <li>Focus on the rotation, internship, program, or supervisory experience — not on attacking individuals.</li>
      <li>Protect the privacy of patients, coworkers, classmates, and other third parties.</li>
      <li>Honest, balanced, and specific feedback helps the community; targeted attacks and fabricated content do not.</li>
    </ul>

    <h2>2. Prohibited Content</h2>

    <h3>Personal attacks and abuse</h3>
    <p>
      Do not post personal attacks, insults, or degrading remarks targeting preceptors, employers,
      instructors, residents, fellow students, or any other individual. Critiquing a person's
      professional conduct in a specific, factual, and proportionate way is permitted; name-calling,
      slurs, and abuse are not.
    </p>

    <h3>Protected, confidential, or private information</h3>
    <p>You must not post:</p>
    <ul>
      <li>Protected health information (PHI) or any identifying information about patients, including names, initials, dates, room numbers, photographs, diagnoses tied to identifiable individuals, or other details that could identify a patient;</li>
      <li>Confidential employer information, internal documents, trade secrets, source code, pricing, or proprietary processes you are not authorized to share;</li>
      <li>Personally identifying information about private individuals — for example, home addresses, personal phone numbers, personal email addresses, government identifiers, or family information;</li>
      <li>Information you are bound by contract, professional ethics rules, FERPA, HIPAA, or other laws not to disclose.</li>
    </ul>

    <h3>False, fabricated, or manipulated content</h3>
    <ul>
      <li>Do not fabricate reviews about programs, employers, or supervisors you did not actually experience;</li>
      <li>Do not post reviews on behalf of someone else as if they were your own;</li>
      <li>Do not coordinate reputation attacks, vote brigades, fake positive or negative campaigns, or any form of manipulation;</li>
      <li>Do not impersonate another person, organization, or Vecurate staff member.</li>
    </ul>

    <h3>Hate, harassment, and threats</h3>
    <ul>
      <li>No threats of violence, calls for harm, or content that promotes self-harm;</li>
      <li>No hate speech or discrimination based on race, ethnicity, national origin, religion, sex, gender identity, sexual orientation, disability, age, or other protected characteristic;</li>
      <li>No targeted harassment, including coordinated negative reviews intended to harm a specific individual.</li>
    </ul>

    <h3>Illegal and harmful content</h3>
    <ul>
      <li>No content that infringes intellectual property rights;</li>
      <li>No sexually explicit content, child sexual abuse material, or content that sexualizes minors;</li>
      <li>No promotion of illegal activity or instructions for causing harm;</li>
      <li>No malware, phishing, or other content intended to compromise users or the Service.</li>
    </ul>

    <h3>Spam and commercial misuse</h3>
    <ul>
      <li>No advertising, promotion, recruiting solicitations, or affiliate links unrelated to a genuine review;</li>
      <li>No mass-posted or duplicate reviews;</li>
      <li>No content posted primarily to manipulate search results or aggregate ratings.</li>
    </ul>

    <h2>3. What Vecurate Does Not Verify</h2>
    <p>
      Vecurate does not independently verify each review, the identity of each reviewer, or each
      statement made in a review. We rely on this Content Policy, automated signals, and reports from
      the community to maintain quality.
    </p>

    <h2>4. Moderation and Removal</h2>
    <p>
      We use a combination of automated tools (such as profanity filters and abuse detection) and human
      review. Vecurate may, at our discretion, remove or restrict any content that violates this
      policy, our Terms, or applicable law, and may suspend or terminate accounts responsible for
      repeated or serious violations.
    </p>
    <p>
      Where appropriate, we will inform the user whose content was removed and the reason. Users may
      appeal a removal by emailing{" "}
      <a href="mailto:support@vecurate.net">support@vecurate.net</a>. We may decline to provide details
      in cases involving safety, ongoing investigations, or legal risk.
    </p>

    <h2>5. Reporting Content</h2>
    <p>
      If you see content that you believe violates this policy, use the report option on the review or
      email <a href="mailto:support@vecurate.net">support@vecurate.net</a>. Please include a link to the
      content and a brief description of the issue.
    </p>
    <p>
      For copyright concerns, see our <a href="/dmca">DMCA / Copyright Policy</a>. For privacy concerns
      involving personal information, email{" "}
      <a href="mailto:support@vecurate.net">support@vecurate.net</a>.
    </p>

    <div className="callout">
      <strong>Reminder:</strong> If you are subject to professional, academic, or institutional
      confidentiality rules, those rules continue to apply when you post on Vecurate. When in doubt,
      leave it out.
    </div>
  </LegalPage>
);

export default ContentPolicyPage;
